import { useState, useEffect, useRef } from 'react'
import { useLocation, useNavigate, Outlet } from 'react-router-dom'
import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'
import ImpersonationPanel from './ImpersonationPanel'
import PresenceAvatars from './PresenceAvatars'
import CommandPalette from './CommandPalette'
import type { CommandItem } from './CommandPalette'
import { useGlobalPresence } from '../hooks/useGlobalPresence'
import { AppShell, useAppShell, LoadingSpinner, Modal } from '@cinqo/ui'
import { CinqoIcon } from './CinqoLogo'
import ImpersonationSearchBar from './shell/ImpersonationSearchBar'
import { NAV_SECTIONS, resolveActive, resolveChild } from './shell/navConfig'
import type { NavChild, NavItem, NavSection } from './shell/navConfig'
import { Breadcrumb, BreadcrumbProvider } from './shell/breadcrumb'
import { prepareImageFile } from '../lib/imagePrep'
import { transformPublicUrl } from '../lib/storageImage'

/* ------------------------------------------------------------------ */
/*  App shell - rail des sections + colonne de section + content area  */
/* ------------------------------------------------------------------ */

interface ShellProps {
  children?: React.ReactNode
}

/* Pages statiques hors routeur (ouvertes dans un nouvel onglet) */
const externalPaths = new Set(NAV_SECTIONS.flatMap(s => s.items.filter(i => i.external).map(i => i.path)))
const openExternal = (path: string) => { window.open(path, '_blank', 'noopener') }

/* ── Command palette items derived from nav (items + leurs children, hors l'enfant qui double l'item) ── */
const commandItems: CommandItem[] = NAV_SECTIONS.flatMap(s =>
  s.items.flatMap(item => [
    {
      id: item.path,
      label: item.label,
      group: s.title,
      path: item.path,
      keywords: item.beta ? ['beta'] : undefined,
    },
    ...(item.children ?? []).filter(child => child.to !== item.path).map(child => ({
      id: child.to,
      label: `${item.label} · ${child.label}`,
      group: s.title,
      path: child.to,
    })),
  ]),
)

/* ------------------------------------------------------------------ */
/*  Shell children                                                      */
/*  - ImpersonationSearchBar → ./shell/ImpersonationSearchBar.tsx      */
/* ------------------------------------------------------------------ */

// ─── Nav item (sidebar desktop + drawer mobile) ─────────────────────────────
// Composants de module, pas des fonctions déclarées dans le rendu du Shell : sinon
// React remonte toute la nav à chaque rendu (changement de route) et la sidebar
// perd sa position de scroll. Utilise useAppShell() pour fermer le drawer mobile.
function NavItemButton({ item, active, onNavigate }: { item: NavItem; active: boolean; onNavigate: (path: string) => void }) {
  const { closeMobileDrawer } = useAppShell()
  const go = (path: string) => { onNavigate(path); closeMobileDrawer() }
  return (
    <button
      onClick={() => item.external ? openExternal(item.path) : go(item.path)}
      title={item.label}
      className={`relative w-full h-9 flex items-center px-2 rounded-lg text-body font-medium transition-colors cursor-pointer ${
        active
          ? 'bg-surface-card shadow-sm text-violet-500'
          : 'text-ink-secondary hover:bg-neutral-100 hover:text-ink'
      }`}
    >
      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
        <item.icon />
      </div>
      <span className="ml-2 whitespace-nowrap flex-1 text-left">{item.label}</span>
      {item.beta && (
        <span className="ml-auto text-label font-bold uppercase px-1.5 py-0.5 rounded-sm bg-violet-50 text-violet-700 tracking-wider">
          Beta
        </span>
      )}
    </button>
  )
}

// ─── Troisieme niveau : children d'un item actif, sous-liste indentee ──────
function NavChildren({ items, activeChild, onNavigate }: { items: NavChild[]; activeChild: NavChild | null; onNavigate: (path: string) => void }) {
  const { closeMobileDrawer } = useAppShell()
  return (
    <div className="ml-8 border-l border-ink-ghost/40 pl-3 space-y-0.5">
      {items.map(child => (
        <button
          key={child.to}
          onClick={() => { onNavigate(child.to); closeMobileDrawer() }}
          title={child.label}
          className={`w-full h-8 flex items-center text-caption text-left whitespace-nowrap transition-colors cursor-pointer ${
            child === activeChild
              ? 'text-violet-500 font-medium'
              : 'text-ink-secondary hover:text-ink'
          }`}
        >
          {child.label}
        </button>
      ))}
    </div>
  )
}

// ─── Items d'une section : item + ses children quand il est actif ──────────
function SectionItems({ section, activePath, activeChild, onNavigate }: { section: NavSection; activePath: string | null; activeChild: NavChild | null; onNavigate: (path: string) => void }) {
  return (
    <div className="px-2 space-y-0.5">
      {section.items.map(item => {
        const active = item.path === activePath
        return (
          <div key={item.path}>
            <NavItemButton item={item} active={active} onNavigate={onNavigate} />
            {active && item.children && (
              <NavChildren items={item.children} activeChild={activeChild} onNavigate={onNavigate} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Section nav (colonne 2 desktop) : titre + items de la section active ──
function SectionNav({ section, activePath, activeChild, onNavigate }: { section: NavSection; activePath: string | null; activeChild: NavChild | null; onNavigate: (path: string) => void }) {
  return (
    <div>
      <div className="h-8 px-4 flex items-center text-label font-semibold uppercase tracking-wider text-ink-muted">
        {section.title}
      </div>
      <SectionItems section={section} activePath={activePath} activeChild={activeChild} onNavigate={onNavigate} />
    </div>
  )
}

// ─── Main nav (drawer mobile) : toutes les sections avec leurs items ───────
function MainNav({ activePath, activeChild, onNavigate }: { activePath: string | null; activeChild: NavChild | null; onNavigate: (path: string) => void }) {
  return (
    <>
      {NAV_SECTIONS.map((section, si) => (
        <div key={section.id}>
          {si > 0 && <div className="mx-3 my-2 border-t border-ink-ghost/30" />}
          <div className="h-6 px-4 flex items-center whitespace-nowrap text-label font-semibold uppercase tracking-wider text-ink-muted">
            {section.title}
          </div>
          <SectionItems section={section} activePath={activePath} activeChild={activeChild} onNavigate={onNavigate} />
        </div>
      ))}
    </>
  )
}

// ─── Rail (colonne 1 desktop) : brand reduit, sections, avatar ─────────────
function Rail({ activeId, onNavigate, avatar }: { activeId: string; onNavigate: (path: string) => void; avatar: React.ReactNode }) {
  return (
    <>
      <button
        onClick={() => onNavigate('/flux')}
        title="Cinqo Ops"
        className="h-12 w-full flex items-center justify-center shrink-0 cursor-pointer hover:opacity-70 transition-opacity"
      >
        <CinqoIcon size={28} variant="dark" />
      </button>
      {NAV_SECTIONS.map(section => {
        const first = section.items[0]
        const active = section.id === activeId
        return (
          <button
            key={section.id}
            onClick={() => first.external ? openExternal(first.path) : onNavigate(first.path)}
            title={section.title}
            className={`w-full h-14 flex flex-col items-center justify-center gap-1 text-label leading-none transition-colors cursor-pointer ${
              active ? 'text-violet-500' : 'text-ink-muted hover:text-ink hover:bg-neutral-200/60'
            }`}
          >
            <span className={`w-[68px] h-12 rounded-lg flex flex-col items-center justify-center gap-1 ${active ? 'bg-surface-card' : ''}`}>
              <section.icon size={20} strokeWidth={1.75} />
              <span className="whitespace-nowrap">{section.title}</span>
            </span>
          </button>
        )
      })}
      <div className="mt-auto pb-2 flex justify-center shrink-0">{avatar}</div>
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Main Shell                                                          */
/* ------------------------------------------------------------------ */

export default function Shell({ children }: ShellProps) {
  const { user, signOut } = useAuth()
  const { team } = useGlobalPresence(user)
  const location = useLocation()
  const navigate = useNavigate()
  // Le drawer mobile est gere par AppShell — useAppShell() donne acces a
  // closeMobileDrawer() depuis les composants de nav enfants.
  const [cmdkOpen, setCmdkOpen] = useState(false)

  // Cmd+K / Ctrl+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setCmdkOpen(prev => !prev)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  // ─── Section active (rail) : derivee de la route, sinon derniere connue ─────
  const resolved = resolveActive(location.pathname)
  const resolvedSection = resolved?.section ?? null
  const [lastSection, setLastSection] = useState<NavSection>(() => resolvedSection ?? NAV_SECTIONS[0])
  useEffect(() => {
    if (resolvedSection) setLastSection(resolvedSection)
  }, [resolvedSection])
  const activeSection = resolvedSection ?? lastSection
  const activePath = resolved?.item.path ?? null
  const activeChild = resolved ? resolveChild(resolved.item, location.pathname, location.search) : null

  // ─── Profil modal + avatar upload ───────────────────────────────────
  const avatarInputRef = useRef<HTMLInputElement>(null)
  const [profileOpen, setProfileOpen] = useState(false)
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    () => user?.user_metadata?.avatar_url ?? null,
  )
  const [avatarUploading, setAvatarUploading] = useState(false)

  useEffect(() => {
    setAvatarUrl(user?.user_metadata?.avatar_url ?? null)
  }, [user?.id])

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.files?.[0]
    if (!raw || !user) return
    setAvatarUploading(true)
    // HEIC iPhone -> JPEG + resize cote client
    const file = (await prepareImageFile(raw)).file
    const ext = file.name.split('.').pop() ?? 'jpg'
    const path = `${user.id}/avatar.${ext}`
    const { error: upErr } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type })
    if (upErr) { console.error('[avatar] upload failed:', upErr.message); setAvatarUploading(false); return }
    // Chemin `bucket/cle` (#1398) ; nom fixe ecrase, `?v=` force le rechargement.
    const storedPath = `avatars/${path}?v=${Date.now()}`
    await supabase.auth.updateUser({ data: { avatar_url: storedPath } })
    setAvatarUrl(storedPath)
    setAvatarUploading(false)
    e.target.value = ''
  }

  const handleRemoveAvatar = async () => {
    if (!user) return
    setAvatarUploading(true)
    await supabase.auth.updateUser({ data: { avatar_url: null } })
    setAvatarUrl(null)
    setAvatarUploading(false)
  }

  const [impersonation, setImpersonation] = useState<{
    type: 'client' | 'partner'
    id: string
    name: string
  } | null>(null)

  // Audit trail impersonation (P0.1 du concern Donald, cf docs/audits/2026-05-20).
  // On wrappe le set/clear pour appeler rpc_log_impersonation cote DB avant
  // de changer le state local. Best-effort : si le RPC echoue (reseau / RLS),
  // on continue quand meme l'action UX pour ne pas bloquer le staff —
  // l'audit trail est important mais non-bloquant.
  async function startImpersonation(target: { type: 'client' | 'partner'; id: string; name: string }) {
    try {
      await supabase.rpc('rpc_log_impersonation', {
        p_event: 'impersonation_started',
        p_entity_type: target.type,
        p_entity_id: target.id,
      })
    } catch (e) {
      console.warn('[impersonation] log start failed', e)
    }
    setImpersonation(target)
  }
  async function endImpersonation() {
    if (impersonation) {
      try {
        await supabase.rpc('rpc_log_impersonation', {
          p_event: 'impersonation_ended',
          p_entity_type: impersonation.type,
          p_entity_id: impersonation.id,
        })
      } catch (e) {
        console.warn('[impersonation] log end failed', e)
      }
    }
    setImpersonation(null)
  }

  if (impersonation) {
    return (
      <ImpersonationPanel
        type={impersonation.type}
        id={impersonation.id}
        name={impersonation.name}
        onClose={endImpersonation}
      />
    )
  }

  // ─── Avatar (rail desktop) : ouvre le modal profil ─────────────────────────
  const RailAvatar = (
    <button
      onClick={() => setProfileOpen(true)}
      title="Mon profil"
      className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center hover:opacity-80 transition-opacity cursor-pointer"
    >
      {avatarUrl ? (
        <img src={transformPublicUrl(avatarUrl, { width: 100 }) ?? avatarUrl} alt={`Avatar de ${user?.email ?? 'utilisateur'}`} className="w-8 h-8 rounded-full object-cover" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-violet-50 text-violet-500 text-label font-bold flex items-center justify-center">
          {(user?.email ?? 'R').substring(0, 2).toUpperCase()}
        </div>
      )}
    </button>
  )

  // ─── Brand (logo + wordmark), utilise dans le header et le drawer mobile ───
  const Brand = (
    <button
      onClick={() => navigate('/flux')}
      className="flex items-center gap-2.5 bg-transparent p-0 border-0 cursor-pointer hover:opacity-70 transition-opacity"
    >
      <CinqoIcon size={28} variant="dark" className="flex-shrink-0" />
      <span className="text-body font-semibold text-ink whitespace-nowrap">Cinqo Ops</span>
    </button>
  )

  // ─── Footer du drawer mobile : profil + deconnexion ────────────────────────
  const SidebarUserFooter = (
    <div className="px-2 py-2 space-y-0.5">
      <button
        onClick={() => setProfileOpen(true)}
        title="Mon profil"
        className="w-full h-10 flex items-center px-2 rounded-lg hover:bg-neutral-100 transition-colors cursor-pointer"
      >
        {avatarUrl ? (
          <img src={transformPublicUrl(avatarUrl, { width: 100 }) ?? avatarUrl} alt={`Avatar de ${user?.email ?? 'utilisateur'}`} className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
        ) : (
          <div className="w-6 h-6 rounded-full bg-violet-50 text-violet-500 text-label font-bold flex items-center justify-center flex-shrink-0">
            {(user?.email ?? 'R').substring(0, 2).toUpperCase()}
          </div>
        )}
        <div className="ml-2 min-w-0 flex-1 whitespace-nowrap">
          <div className="text-caption font-medium text-violet-500 truncate leading-tight">Regulateur</div>
          <div className="text-label text-ink-muted truncate leading-tight">{user?.email ?? ''}</div>
        </div>
      </button>
      <button
        onClick={signOut}
        title="Déconnexion"
        className="w-full h-9 flex items-center px-2 rounded-lg text-caption text-ink-muted hover:bg-neutral-100 hover:text-ink-secondary transition-colors cursor-pointer"
      >
        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15m3 0l3-3m0 0l-3-3m3 3H9" />
          </svg>
        </div>
        <span className="ml-2 whitespace-nowrap">Déconnexion</span>
      </button>
    </div>
  )

  // ─── Badge environnement (visible uniquement en staging) ────────────
  // Permet de distinguer visuellement staging.ops.cinqo.fr de ops.cinqo.fr
  // Lit VITE_SENTRY_ENVIRONMENT plumbe via CF Pages env vars (cf docs/staging.md).
  const envTag = import.meta.env.VITE_SENTRY_ENVIRONMENT as string | undefined
  const StagingBadge = envTag === 'staging' ? (
    <span
      title="Vous etes sur l'environnement de staging — les emails sont rediriges, ce n'est PAS la prod"
      className="px-2 py-0.5 rounded-md bg-orange-100 text-orange-700 text-label font-bold uppercase tracking-wider border border-orange-300"
    >
      Staging
    </span>
  ) : null

  // ─── Topbar left (desktop) : fil Section › Écran › segments de la page ──
  const TopbarLeft = <Breadcrumb />

  // ─── Topbar right (desktop) : Cmd+K, presence ──────────────────────
  const TopbarRight = (
    <>
      {StagingBadge}
      <button
        onClick={() => setCmdkOpen(true)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-ink-ghost/30 bg-surface-low hover:bg-surface-mid hover:border-ink-ghost transition-colors cursor-pointer"
      >
        <span className="text-caption text-ink-muted">Rechercher</span>
        <kbd className="px-1.5 py-0.5 rounded-md bg-surface-card border border-ink-ghost/30 text-label font-medium text-ink-muted">⌘K</kbd>
      </button>
      <div className="w-px h-5 bg-ink-ghost/30" />
      <PresenceAvatars team={team} currentUserId={user?.id} />
    </>
  )

  // ─── Topbar right (mobile) : presence + logout compact ────────────
  const MobileTopbarRight = (
    <>
      {StagingBadge}
      <PresenceAvatars team={team} currentUserId={user?.id} max={3} />
      <button onClick={signOut} className="text-caption text-ink-muted cursor-pointer">Déconnexion</button>
    </>
  )

  return (
    <>
      <BreadcrumbProvider>
      <AppShell
        layout="rail"
        brand={Brand}
        rail={<Rail activeId={activeSection.id} onNavigate={navigate} avatar={RailAvatar} />}
        nav={<SectionNav section={activeSection} activePath={activePath} activeChild={activeChild} onNavigate={navigate} />}
        mobileNav={<MainNav activePath={activePath} activeChild={activeChild} onNavigate={navigate} />}
        sidebarHeader={
          <div className="px-2 pt-2 pb-1">
            <ImpersonationSearchBar
              onSelect={(r) => startImpersonation({ type: r.type, id: r.id, name: r.nom })}
            />
          </div>
        }
        sidebarFooter={SidebarUserFooter}
        topbarLeft={TopbarLeft}
        topbarRight={TopbarRight}
        mobileTopbarRight={MobileTopbarRight}
      >
        <div className="p-6 sm:p-8">
          <Outlet context={{ setImpersonation }} />
          {children}
        </div>
      </AppShell>
      </BreadcrumbProvider>

      {/* ── Floating global overlays ──────────────────────────────────────── */}

      {/* ── Profile modal ─────────────────────────────────────────────── */}
      {profileOpen && (
        <Modal
          open
          onClose={() => setProfileOpen(false)}
          title="Mon profil"
          size="sm"
          zIndex={300}
          footer={
            <div className="flex justify-between items-center w-full">
              <button
                onClick={() => { setProfileOpen(false); signOut() }}
                className="text-caption text-error hover:text-error/80 font-medium transition-colors cursor-pointer"
              >
                Se deconnecter
              </button>
              <button
                onClick={() => setProfileOpen(false)}
                className="px-4 py-1.5 text-caption font-medium bg-surface-low text-ink-secondary rounded-md hover:bg-surface-mid transition-colors cursor-pointer"
              >
                Fermer
              </button>
            </div>
          }
        >
          <div className="space-y-5">
            {/* Avatar section */}
            <div className="flex items-center gap-4">
              <div className="relative group">
                {avatarUrl ? (
                  <img src={transformPublicUrl(avatarUrl, { width: 100 }) ?? avatarUrl} alt={`Avatar de ${user?.email ?? 'utilisateur'}`} className="w-16 h-16 rounded-full object-cover border-2 border-ink-ghost/30" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-violet-50 text-violet-500 text-h3 font-bold flex items-center justify-center border-2 border-ink-ghost/30">
                    {(user?.email ?? 'R').substring(0, 2).toUpperCase()}
                  </div>
                )}
                {avatarUploading && (
                  <div className="absolute inset-0 rounded-full bg-surface-card/70 flex items-center justify-center">
                    <LoadingSpinner size="md" />
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <button
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={avatarUploading}
                  className="px-3 py-1.5 text-caption font-medium bg-violet-500 text-white rounded-md hover:bg-violet-600 transition-colors disabled:opacity-50 cursor-pointer"
                >
                  {avatarUrl ? 'Changer la photo' : 'Ajouter une photo'}
                </button>
                {avatarUrl && (
                  <button
                    onClick={handleRemoveAvatar}
                    disabled={avatarUploading}
                    className="block px-3 py-1 text-caption text-error hover:text-error/80 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    Supprimer
                  </button>
                )}
              </div>
            </div>

            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAvatarChange}
            />

            {/* User info */}
            <div className="space-y-3 pt-2 border-t border-ink-ghost/30">
              <div>
                <div className="text-label font-medium text-ink-muted uppercase tracking-wider mb-1">Role</div>
                <div className="text-body text-ink-secondary">Regulateur</div>
              </div>
              <div>
                <div className="text-label font-medium text-ink-muted uppercase tracking-wider mb-1">Email</div>
                <div className="text-body text-ink-secondary">{user?.email ?? '—'}</div>
              </div>
              <div>
                <div className="text-label font-medium text-ink-muted uppercase tracking-wider mb-1">Identifiant</div>
                <div className="text-caption text-ink-muted font-mono">{user?.id ?? '—'}</div>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Command Palette (Cmd+K) ───────────────────────────────────── */}
      <CommandPalette
        open={cmdkOpen}
        onClose={() => setCmdkOpen(false)}
        items={commandItems}
        onSelect={item => externalPaths.has(item.path) ? openExternal(item.path) : navigate(item.path)}
      />
    </>
  )
}
